import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Trash2, Minus, Plus, ShoppingBag, Loader2, Lock } from "lucide-react";
import { useCart, inr } from "../../store/CartContext";
import { track } from "../../lib/track";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-dwc-charcoal outline-none transition-colors focus:border-dwc-green";

const useSettings = () => {
  const [settings, setSettings] = useState({ cod_enabled: true, shipping_fee: 49, free_shipping_above: 999, delivery_estimate: "" });
  useEffect(() => {
    axios.get(`${API}/api/store/settings`).then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  return settings;
};

const Totals = ({ subtotal, settings }) => {
  const shipping = subtotal >= settings.free_shipping_above ? 0 : settings.shipping_fee;
  return (
    <div className="space-y-2 text-sm font-semibold text-dwc-charcoal/75">
      <p className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal">{inr(subtotal)}</span></p>
      <p className="flex justify-between"><span>Shipping</span><span data-testid="cart-shipping">{shipping === 0 ? "FREE" : inr(shipping)}</span></p>
      {shipping > 0 && <p className="text-[11px] text-dwc-green">Free shipping on orders above {inr(settings.free_shipping_above)}</p>}
      <p className="flex justify-between border-t border-gray-100 pt-3 text-base font-black text-dwc-charcoal"><span>Total</span><span data-testid="cart-total">{inr(subtotal + shipping)}</span></p>
    </div>
  );
};

export const StoreCart = () => {
  const { items, setQty, remove, subtotal } = useCart();
  const settings = useSettings();

  if (items.length === 0)
    return (
      <div className="px-5 pb-28 pt-44 text-center" data-testid="empty-cart">
        <ShoppingBag className="mx-auto h-12 w-12 text-dwc-green/30" aria-hidden="true" />
        <p className="mt-4 text-lg font-extrabold text-dwc-charcoal">Your cart is empty.</p>
        <Link to="/store/shop" data-testid="empty-cart-shop-btn" className="mt-5 inline-block rounded-xl bg-dwc-green px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white">
          Shop DWC Store
        </Link>
      </div>
    );

  return (
    <section className="bg-dwc-soft pb-24 pt-32 md:pt-40" data-testid="cart-page">
      <div className="mx-auto max-w-5xl px-5 lg:px-8">
        <h1 className="text-3xl font-extrabold tracking-tight text-dwc-charcoal">Your Cart</h1>
        <div className="mt-8 grid gap-6 lg:grid-cols-[1.5fr_1fr]">
          <div className="space-y-3">
            {items.map((i) => (
              <div key={i.id} className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-4" data-testid={`cart-item-${i.slug}`}>
                <img src={i.image} alt={i.name} className="h-20 w-20 rounded-xl bg-dwc-soft object-cover" loading="lazy" />
                <div className="min-w-0 flex-1">
                  <Link to={`/store/product/${i.slug}`} className="block truncate text-sm font-extrabold text-dwc-charcoal hover:text-dwc-green">{i.name}</Link>
                  <p className="mt-1 text-sm font-black text-dwc-green">{inr(i.price)}</p>
                  <div className="mt-2 flex items-center gap-3">
                    <div className="flex items-center rounded-lg border border-gray-200">
                      <button onClick={() => setQty(i.id, i.qty - 1)} aria-label="Decrease" data-testid={`cart-dec-${i.slug}`} className="px-2.5 py-1.5 text-dwc-charcoal/60"><Minus className="h-3.5 w-3.5" aria-hidden="true" /></button>
                      <span className="min-w-[2rem] text-center text-xs font-black">{i.qty}</span>
                      <button onClick={() => setQty(i.id, i.qty + 1)} aria-label="Increase" data-testid={`cart-inc-${i.slug}`} className="px-2.5 py-1.5 text-dwc-charcoal/60"><Plus className="h-3.5 w-3.5" aria-hidden="true" /></button>
                    </div>
                    <button onClick={() => remove(i.id)} aria-label={`Remove ${i.name}`} data-testid={`cart-remove-${i.slug}`} className="text-dwc-charcoal/40 transition-colors hover:text-dwc-crimson"><Trash2 className="h-4 w-4" aria-hidden="true" /></button>
                  </div>
                </div>
                <p className="text-sm font-black text-dwc-charcoal">{inr(i.price * i.qty)}</p>
              </div>
            ))}
          </div>
          <div className="h-fit rounded-2xl border border-gray-100 bg-white p-6">
            <h2 className="text-sm font-extrabold uppercase tracking-wide text-dwc-charcoal">Order Summary</h2>
            <div className="mt-4">
              <Totals subtotal={subtotal} settings={settings} />
            </div>
            <Link
              to="/store/checkout"
              data-testid="proceed-to-checkout-btn"
              className="mt-6 block rounded-xl bg-dwc-green py-4 text-center text-sm font-bold uppercase tracking-wide text-white shadow-[0_8px_20px_rgba(23,107,58,0.28)] transition-colors hover:bg-dwc-green-dark"
            >
              Proceed to Checkout
            </Link>
            <Link to="/store/shop" className="mt-3 block text-center text-[12px] font-bold uppercase tracking-wide text-dwc-green">Continue Shopping</Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export const StoreCheckout = () => {
  const { items, subtotal, clear } = useCart();
  const settings = useSettings();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", mobile: "", email: "", address: "", city: "", state: "Gujarat", pincode: "" });
  const [payment, setPayment] = useState("card");
  const [agree, setAgree] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (items.length === 0)
    return (
      <div className="px-5 pb-28 pt-44 text-center">
        <p className="text-lg font-extrabold text-dwc-charcoal">Your cart is empty.</p>
        <Link to="/store/shop" className="mt-4 inline-block text-sm font-bold uppercase tracking-wide text-dwc-green">Shop DWC Store</Link>
      </div>
    );

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) {
      setError("Please accept the store terms to place your order.");
      return;
    }
    setStatus("submitting");
    setError("");
    try {
      const res = await axios.post(`${API}/api/store/orders`, {
        items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
        customer: form,
        payment_method: payment,
        origin_url: window.location.origin,
      });
      track("store_purchase_initiated", { payment, total: res.data.total });
      if (payment === "card" && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      const order = { order_number: res.data.order_number, total: res.data.total, items, customer: form, payment_method: payment };
      clear();
      navigate("/store/order-confirmed", { state: { order } });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      setStatus("idle");
    }
  };

  return (
    <section className="bg-dwc-soft pb-24 pt-32 md:pt-40" data-testid="checkout-page">
      <div className="mx-auto max-w-5xl px-5 lg:px-8">
        <h1 className="text-3xl font-extrabold tracking-tight text-dwc-charcoal">Checkout</h1>
        <form onSubmit={submit} className="mt-8 grid gap-6 lg:grid-cols-[1.5fr_1fr]">
          <div className="space-y-4 rounded-2xl border border-gray-100 bg-white p-6">
            <h2 className="text-sm font-extrabold uppercase tracking-wide text-dwc-charcoal">Delivery Details</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <input required minLength={2} value={form.name} onChange={set("name")} placeholder="Full Name *" aria-label="Full name" data-testid="checkout-name" className={inputCls} />
              <input required minLength={8} type="tel" value={form.mobile} onChange={set("mobile")} placeholder="Mobile Number *" aria-label="Mobile number" data-testid="checkout-mobile" className={inputCls} />
            </div>
            <input type="email" value={form.email} onChange={set("email")} placeholder="Email (optional)" aria-label="Email" data-testid="checkout-email" className={inputCls} />
            <input required minLength={5} value={form.address} onChange={set("address")} placeholder="Full Address *" aria-label="Address" data-testid="checkout-address" className={inputCls} />
            <div className="grid gap-4 sm:grid-cols-3">
              <input required minLength={2} value={form.city} onChange={set("city")} placeholder="City *" aria-label="City" data-testid="checkout-city" className={inputCls} />
              <input required minLength={2} value={form.state} onChange={set("state")} placeholder="State *" aria-label="State" data-testid="checkout-state" className={inputCls} />
              <input required minLength={4} value={form.pincode} onChange={set("pincode")} placeholder="PIN Code *" aria-label="PIN code" data-testid="checkout-pincode" className={inputCls} />
            </div>
            <h2 className="pt-2 text-sm font-extrabold uppercase tracking-wide text-dwc-charcoal">Payment Method</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 p-4 transition-colors ${payment === "card" ? "border-dwc-green bg-dwc-soft" : "border-gray-200"}`}>
                <input type="radio" name="payment" checked={payment === "card"} onChange={() => setPayment("card")} data-testid="payment-card" className="accent-dwc-green" />
                <span>
                  <span className="block text-sm font-extrabold text-dwc-charcoal">Pay Online (Card)</span>
                  <span className="block text-[12px] text-dwc-charcoal/60">Secure checkout via Stripe</span>
                </span>
              </label>
            </div>
            <label className="flex items-start gap-2.5 pt-2 text-[12px] font-semibold text-dwc-charcoal/70">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="checkout-terms-checkbox" className="mt-0.5 accent-dwc-green" />
              <span>
                I agree to the DWC Store <Link to="/store/policies" className="underline hover:text-dwc-green">terms, shipping and return policies</Link>.
              </span>
            </label>
            {error && <p className="rounded-lg bg-dwc-crimson/[0.06] px-4 py-3 text-sm font-bold text-dwc-crimson" data-testid="checkout-error">{error}</p>}
          </div>
          <div className="h-fit rounded-2xl border border-gray-100 bg-white p-6">
            <h2 className="text-sm font-extrabold uppercase tracking-wide text-dwc-charcoal">Order Summary</h2>
            <div className="mt-4 space-y-2">
              {items.map((i) => (
                <p key={i.id} className="flex justify-between text-[13px] font-semibold text-dwc-charcoal/75">
                  <span className="truncate pr-3">{i.name} × {i.qty}</span>
                  <span>{inr(i.price * i.qty)}</span>
                </p>
              ))}
            </div>
            <div className="mt-4 border-t border-gray-100 pt-4">
              <Totals subtotal={subtotal} settings={settings} />
            </div>
            <button
              type="submit"
              disabled={status === "submitting"}
              data-testid="place-order-btn"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-dwc-green py-4 text-sm font-bold uppercase tracking-wide text-white shadow-[0_8px_20px_rgba(23,107,58,0.28)] transition-colors hover:bg-dwc-green-dark disabled:opacity-60"
            >
              {status === "submitting" ? (
                <><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Placing Order...</>
              ) : payment === "card" ? (
                <><Lock className="h-4 w-4" aria-hidden="true" /> Pay & Place Order</>
              ) : (
                "Place Order"
              )}
            </button>
            <p className="mt-3 text-center text-[11px] text-dwc-charcoal/50">{settings.delivery_estimate ? `Delivery: ${settings.delivery_estimate}` : "Secure checkout via Stripe"}</p>
          </div>
        </form>
      </div>
    </section>
  );
};

export default StoreCart;
